<main>
  <header>
    <h1>Business Structure</h1>
    <h2>Tell us about your business, so we can better support you with a setup that fits your needs.</h2>
  </header>

  <form class="application-form" [formGroup]="form" (ngSubmit)="continue()" appPopulateForm [formType]="'businessStructure'" [form]="form">

    <!-- Business Structure Field -->
    <div class="field">
      <label for="businessStructure">Business Structure</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <mat-select formControlName="businessStructure" required placeholder="Enter">
          @for (option of businessStructureOptions; track option) {
            <mat-option [value]="option.value">{{option.label}}</mat-option>
          }
        </mat-select>
        @if (form.get('businessStructure')?.hasError('required')) {
          <mat-error>
            Business Structure is required
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- SSN -->
    @if (role === 'supplier') {
      <div class="field">
        <label for="businessSsn">SSN</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="businessSsn" mask="XXX-XX-XXXX" [hiddenInput]="ssnMasked"
            [patterns]="ssnReplacePattern" placeholder="Enter"
            (keydown)="onSsnKeyDown($event)" (paste)="onSsnPaste($event)">
          <button type="button" mat-icon-button class="ssn-eye" matSuffix (click)="toggleSSNVisibility($event)" matTooltip="Show/Hide SSN">
            <img src="icons/lock.svg" alt="Lock Icon" />
          </button>
          @if (form.get('businessSsn')?.hasError('pattern') || form.get('businessSsn')?.hasError('ssn')) {
            <mat-error>
              SSN must be 9 digits
            </mat-error>
          }
          @if (form.get('businessSsn')?.hasError('required')) {
            <mat-error>
              SSN is required
            </mat-error>
          }
          @if (form.get('businessSsn')?.hasError('mask')) {
            <mat-error>
              SSN must be in the format XXX-XX-XXXX
            </mat-error>
          }
        </mat-form-field>
        @if (isSoleProp) {
          <p class="field-info">Your SSN helps us verify your details and prevent fraud. We'll keep your data secure.</p>
        }
        @if (!isSoleProp) {
          <p class="field-info">Providing your SSN (or ITIN) is optional unless you are a sole proprietor. However, including it may help speed up underwriting and certain automated verification checks.</p>
        }
      </div>
    }

    <!-- Business Tax ID Field -->
    @if (!isSoleProp) {
      <div class="field">
        <label for="businessTaxID">Business Tax ID</label>
        <mat-form-field appearance="outline" hideRequiredMarker>
          <input matInput formControlName="businessTaxID" placeholder="Enter" mask="00-0000000">
          @if (form.get('businessTaxID')?.hasError('required')) {
            <mat-error>
              Business Tax ID is required
            </mat-error>
          }
          @if (form.get('businessTaxID')?.hasError('pattern')) {
            <mat-error>
              Business Tax ID must be exactly 9 digits
            </mat-error>
          }
        </mat-form-field>
        <p class="field-info">We need to ask for your Employer Identification Number to verify your identity. We'll keep your data secure.</p>
      </div>
    }

    <!-- Industry Field -->
    <div class="field">
      <label for="industry">Industry</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <mat-select formControlName="industryType" required placeholder="Enter">
          @for (option of industryOptions; track option) {
            <mat-option [value]="option.value">{{option.label}}</mat-option>
          }
        </mat-select>
        @if (form.get('industryType')?.hasError('required')) {
          <mat-error>
            Industry is required
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Years in Business Field -->
    <div class="field">
      <label for="yearsInBusiness">Years in Business</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <mat-select formControlName="yearsInBusiness" required placeholder="Enter">
          @for (option of yearsInBusinessOptions; track option) {
            <mat-option [value]="option.value">{{option.label}}</mat-option>
          }
        </mat-select>
        @if (form.get('yearsInBusiness')?.hasError('required')) {
          <mat-error>
            Years in Business is required
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Number of Employees Field -->
    <div class="field">
      <label for="numberOfEmployees">Number of Employees</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <input matInput formControlName="numberOfEmployees" required type="number" min="1" placeholder="Enter" (keypress)="onlyNumberKey($event)">
        @if (form.get('numberOfEmployees')?.hasError('required')) {
          <mat-error>
            Number of Employees is required
          </mat-error>
        }
        @if (form.get('numberOfEmployees')?.hasError('min')) {
          <mat-error>
            Number must be 1 or higher
          </mat-error>
        }
      </mat-form-field>
      <p class="field-info">Include both full-time and part-time employees.</p>
    </div>

    <!-- Gross Annual Revenue Field -->
    <div class="field">
      <label for="grossAnnualRevenue">Gross Annual Revenue</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <!-- prefix the input with a $ -->
        <span matPrefix>&nbsp;</span>
        <!-- bind the displayed value through the currency pipe -->
        <input
          matInput
          [value]="grossAnnualRevenueDisplay"
          (input)="onRevenueInput($event)"
          (blur)="onRevenueBlur()"
          required
          placeholder="$0.00"
          />
        @if (form.get('grossAnnualRevenue')?.hasError('required')) {
          <mat-error>
            Gross Annual Revenue is required
          </mat-error>
        }
        @if (form.get('grossAnnualRevenue')?.hasError('min')) {
          <mat-error>
            Value must be greater than 0
          </mat-error>
        }
        @if (form.get('grossAnnualRevenue')?.hasError('max')) {
          <mat-error>
            Gross Annual Revenue cannot exceed 15 digits
          </mat-error>
        }
      </mat-form-field>
    </div>


    <!-- Referral Number Field (Optional) -->
    <div class="field">
      <label for="referralNumber">Referral Number</label>
      <mat-form-field appearance="outline" hideRequiredMarker>
        <input matInput formControlName="referralNumber" placeholder="(optional)">
        @if (form.get('referralNumber')?.hasError('maxlength')) {
          <mat-error>
            Referral Number cannot exceed 15 characters
          </mat-error>
        }
      </mat-form-field>
    </div>

    <app-form-actions
      [isFormInvalid]="form.invalid"
      (continue)="continue()"
      (back)="goBack()"
      >
    </app-form-actions>
  </form>
</main>
